import React, { useState, useCallback, useRef } from 'react';
import { 
  Evidence, 
  EvidenceType, 
  UploadStatus,
  FILE_SIZE_LIMITS,
  SUPPORTED_FILE_TYPES,
  MIME_TYPES
} from '../../types/audit-trail';
import { validationService } from '../../lib/audit-trail/validation';
import { decisionCrypto } from '../../lib/audit-trail/decision-crypto';
import { offlineManager } from '../../lib/audit-trail/offline-manager';
import { cn, isMobile, formatFileSize } from '../../lib/utils';

interface EvidenceUploaderProps {
  evidence: Evidence[];
  onEvidenceChange: (evidence: Evidence[]) => void;
  decisionId?: string;
  disabled?: boolean;
  maxFiles?: number;
  className?: string;
}

interface UploadProgress {
  [fileId: string]: number;
}

export const EvidenceUploader: React.FC<EvidenceUploaderProps> = ({
  evidence,
  onEvidenceChange,
  decisionId,
  disabled = false,
  maxFiles = 10,
  className = ''
}) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [dragActive, setDragActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isSwipeView, setIsSwipeView] = useState(isMobile());
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Handle window resize for mobile view
  React.useEffect(() => {
    const handleResize = () => {
      setIsSwipeView(isMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Cleanup camera stream on unmount
  React.useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Calculate current total size
  const currentTotalSize = evidence.reduce((total, item) => total + item.size, 0);
  const remainingSize = FILE_SIZE_LIMITS.maxTotalSize - currentTotalSize;

  const generateEvidenceId = () => decisionCrypto.generateSecureId();

  const getFileType = (file: File): EvidenceType => {
    if (file.type.startsWith('image/')) return 'photo';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.startsWith('video/')) return 'photo'; // Treat videos as photos for now
    return 'document';
  };

  const generateThumbnail = async (file: File): Promise<string | undefined> => {
    if (!file.type.startsWith('image/')) return undefined;

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate thumbnail size (max 150px)
        const maxSize = 150;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      
      img.onerror = () => resolve(undefined);
      img.src = URL.createObjectURL(file);
    });
  };

  const validateFiles = (files: File[]): { valid: File[]; errors: string[] } => {
    const errors: string[] = [];
    const valid: File[] = [];

    // Check file count limit
    if (evidence.length + files.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed. You can upload ${maxFiles - evidence.length} more files.`);
      return { valid, errors };
    }

    for (const file of files) {
      // Validate individual file
      const fileErrors = validationService.validateEvidenceFile(file);
      if (fileErrors.length > 0) {
        errors.push(...fileErrors.map(e => e.message));
        continue;
      }

      // Check total size
      const totalSizeErrors = validationService.validateTotalEvidenceSize(evidence, file);
      if (totalSizeErrors.length > 0) {
        errors.push(...totalSizeErrors.map(e => e.message));
        continue;
      }

      // Check for duplicate names
      if (evidence.some(e => e.filename === file.name)) {
        errors.push(`File "${file.name}" already exists`);
        continue;
      }

      valid.push(file);
    }

    return { valid, errors };
  };

  const processFile = async (file: File): Promise<Evidence> => {
    const evidenceId = generateEvidenceId();
    const fileType = getFileType(file);
    
    // Generate thumbnail for images
    const thumbnail = await generateThumbnail(file);
    
    // Get file metadata
    const metadata: any = {};
    
    if (fileType === 'photo' && file.type.startsWith('image/')) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise(resolve => {
        img.onload = () => {
          metadata.dimensions = { width: img.width, height: img.height };
          URL.revokeObjectURL(img.src);
          resolve(null);
        };
      });
    }
    
    if (fileType === 'audio') {
      try {
        const duration = await validationService['getAudioDuration'](file);
        metadata.duration = duration;
      } catch (e) {
        // Audio duration detection failed, continue without it
      }
    }

    const evidence: Evidence = {
      id: evidenceId,
      type: fileType,
      filename: file.name,
      size: file.size,
      mimeType: file.type,
      thumbnail,
      uploadStatus: 'pending',
      metadata
    };

    // Store file locally if offline
    if (offlineManager.isOffline()) {
      await offlineManager.saveEvidence(evidence, file);
    }

    return evidence;
  };

  const uploadFile = async (evidence: Evidence, file: File): Promise<Evidence | void> => {
    if (offlineManager.isOffline()) {
      // File already stored locally in processFile
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('evidenceId', evidence.id);
    formData.append('decisionId', decisionId || '');

    try {
      setUploadProgress(prev => ({ ...prev, [evidence.id]: 0 }));

      const response = await fetch('/api/decisions/evidence', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Update evidence with server URL
      const updatedEvidence = { ...evidence, url: result.url, uploadStatus: 'complete' as const };
      
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[evidence.id];
        return newProgress;
      });

      return updatedEvidence;
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[evidence.id];
        return newProgress;
      });
      throw error;
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const errors = getDetailedValidationErrors(fileArray);

    if (errors.length > 0) {
      // Show better error UI for mobile
      if (isSwipeView) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'fixed top-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 z-50';
        errorDiv.innerHTML = `
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h4 class="font-semibold text-red-800 mb-2">Upload Error</h4>
              <ul class="text-sm text-red-700 space-y-1">
                ${errors.map(error => `<li>• ${error}</li>`).join('')}
              </ul>
            </div>
            <button onclick="this.parentElement.parentElement.remove()" class="text-red-400 hover:text-red-600">×</button>
          </div>
        `;
        document.body.appendChild(errorDiv);
        setTimeout(() => errorDiv.remove(), 5000);
      } else {
        alert(`Upload errors:\n${errors.join('\n')}`);
      }
      return;
    }

    // Filter valid files
    const validFiles = fileArray.filter(file => {
      const allowedTypes = Object.keys(MIME_TYPES);
      return file.size <= FILE_SIZE_LIMITS.maxFileSize && 
             allowedTypes.includes(file.type) &&
             !evidence.some(e => e.filename === file.name);
    });

    const newEvidence: Evidence[] = [];

    for (const file of validFiles) {
      try {
        const evidenceItem = await processFile(file);
        newEvidence.push(evidenceItem);

        // Start upload if online
        if (!offlineManager.isOffline()) {
          uploadFile(evidenceItem, file).then((updated) => {
            if (updated) {
              onEvidenceChange(
                evidence.map(e => e.id === evidenceItem.id ? updated : e)
              );
            }
          }).catch((error) => {
            // Update status to failed
            onEvidenceChange(
              evidence.map(e => 
                e.id === evidenceItem.id 
                  ? { ...e, uploadStatus: 'failed' } 
                  : e
              )
            );
          });
        }
      } catch (error) {
        console.error('Failed to process file:', file.name, error);
        // Show mobile-friendly error
        if (isSwipeView) {
          const toast = document.createElement('div');
          toast.className = 'fixed bottom-20 left-4 right-4 bg-red-500 text-white p-3 rounded-lg z-50';
          toast.textContent = `Failed to process: ${file.name}`;
          document.body.appendChild(toast);
          setTimeout(() => toast.remove(), 3000);
        } else {
          alert(`Failed to process file: ${file.name}`);
        }
      }
    }

    onEvidenceChange([...evidence, ...newEvidence]);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (disabled) return;
    
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeEvidence = (evidenceId: string) => {
    onEvidenceChange(evidence.filter(e => e.id !== evidenceId));
    
    // Cancel upload if in progress
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[evidenceId];
      return newProgress;
    });
  };

  const retryUpload = async (evidenceItem: Evidence) => {
    // This would require storing the original file, which we don't do for security
    // In a real implementation, you might want to store files temporarily
    alert('Please re-upload the file to retry');
  };

  // Audio recording functionality
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
        
        await handleFiles([file]);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= FILE_SIZE_LIMITS.maxAudioDuration) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Camera functionality
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Failed to access camera:', error);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
    setCapturedPhotos([]);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
            await handleFiles([file]);
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };

  // Enhanced preview functionality
  const showImagePreview = (evidence: Evidence) => {
    if (evidence.thumbnail) {
      setPreviewImage(evidence.thumbnail);
      setShowPreview(true);
    }
  };

  const closePreview = () => {
    setPreviewImage(null);
    setShowPreview(false);
  };

  // File type validation with better error messages
  const getDetailedValidationErrors = (files: File[]): string[] => {
    const errors: string[] = [];
    
    // Check total files limit
    if (evidence.length + files.length > maxFiles) {
      errors.push(`Too many files. Maximum ${maxFiles} files allowed. You can upload ${maxFiles - evidence.length} more files.`);
      return errors;
    }

    // Check total size limit
    const currentSize = evidence.reduce((total, e) => total + e.size, 0);
    const newSize = files.reduce((total, f) => total + f.size, 0);
    
    if (currentSize + newSize > FILE_SIZE_LIMITS.maxTotalSize) {
      const remainingSize = FILE_SIZE_LIMITS.maxTotalSize - currentSize;
      errors.push(`Total size limit exceeded. You have ${formatFileSize(remainingSize)} remaining space.`);
      return errors;
    }

    // Validate each file
    files.forEach(file => {
      // File size check
      if (file.size > FILE_SIZE_LIMITS.maxFileSize) {
        errors.push(`"${file.name}" is too large. Maximum file size is ${formatFileSize(FILE_SIZE_LIMITS.maxFileSize)}.`);
        return;
      }

      // File type check
      const allowedTypes = Object.keys(MIME_TYPES);
      if (!allowedTypes.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported file type.`);
        return;
      }

      // Duplicate name check
      if (evidence.some(e => e.filename === file.name)) {
        errors.push(`"${file.name}" already exists. Please rename or choose a different file.`);
        return;
      }
    });

    return errors;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFileIcon = (type: EvidenceType): React.ReactNode => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case 'photo': return <div className={cn(iconClass, "bg-terracotta-500 rounded")}></div>;
      case 'audio': return <div className={cn(iconClass, "bg-amber-500 rounded-full")}></div>;
      case 'document': return <div className={cn(iconClass, "bg-slate-500 rounded-sm")}></div>;
      case 'screenshot': return <div className={cn(iconClass, "bg-steel-500 rounded")}></div>;
      default: return <div className={cn(iconClass, "bg-sage-500 rounded-full")}></div>;
    }
  };

  const getStatusIcon = (status: UploadStatus): React.ReactNode => {
    const iconClass = "w-3 h-3";
    switch (status) {
      case 'pending': return <div className={cn(iconClass, "bg-terracotta-500 rounded")}></div>;
      case 'uploading': return <div className={cn(iconClass, "bg-steel-500 rounded-full animate-pulse")}></div>;
      case 'complete': return <div className={cn(iconClass, "bg-sage-500 rounded-full")}></div>;
      case 'failed': return <div className={cn(iconClass, "bg-coral-500 rounded-sm")}></div>;
      default: return <div className={cn(iconClass, "bg-slate-500 rounded-full")}></div>;
    }
  };

  // Camera Modal Component
  const CameraModal = () => {
    if (!showCamera) return null;
    return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Take Photo</h3>
          <button onClick={stopCamera} className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center">
            <div className="w-3 h-3 bg-gray-600 rounded-sm transform rotate-45"></div>
          </button>
        </div>
        
        <div className="relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full rounded-lg"
            style={{ transform: 'scaleX(-1)' }} // Mirror for selfie-like experience
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
        
        <div className="flex justify-center mt-4">
          <button
            onClick={capturePhoto}
            className="w-16 h-16 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
    );
  };

  // Image Preview Modal
  const ImagePreviewModal = () => {
    if (!showPreview || !previewImage) return null;
    return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="relative max-w-4xl max-h-full">
        <img
          src={previewImage}
          alt="Preview"
          className="max-w-full max-h-full rounded-lg"
        />
        <button
          onClick={closePreview}
          className="absolute top-2 right-2 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-75"
        >
          <div className="w-4 h-4 bg-white rounded-sm transform rotate-45"></div>
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className={cn("evidence-uploader", className, isSwipeView && "mobile-evidence")}>
      <div className={cn("uploader-header", isSwipeView ? "p-4 border-b border-gray-200" : "mb-6")}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-sage-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Evidence & Attachments</h3>
          </div>
          <div className="text-sm text-gray-600">
            {evidence.length}/{maxFiles} files
          </div>
        </div>
        
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Used: {formatFileSize(currentTotalSize)}
          </span>
          <span className="text-gray-500">
            Limit: {formatFileSize(FILE_SIZE_LIMITS.maxTotalSize)}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-orange-500 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${(currentTotalSize / FILE_SIZE_LIMITS.maxTotalSize) * 100}%` }}
          />
        </div>
      </div>

      {/* Mobile Action Buttons */}
      {isSwipeView && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="flex flex-col items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <div className="w-8 h-8 mb-2 bg-blue-500 rounded-lg flex items-center justify-center">
              <div className="w-5 h-4 bg-white rounded-sm"></div>
            </div>
            <div className="text-sm font-medium text-blue-800">Browse Files</div>
          </button>
          
          <button
            onClick={startCamera}
            disabled={disabled}
            className="flex flex-col items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <div className="w-8 h-8 mb-2 bg-green-500 rounded-lg flex items-center justify-center">
              <div className="w-5 h-5 bg-white rounded-full"></div>
            </div>
            <div className="text-sm font-medium text-green-800">Take Photo</div>
          </button>
          
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled}
            className={cn(
              "flex flex-col items-center p-4 border rounded-lg disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed transition-colors",
              isRecording 
                ? "bg-red-50 border-red-200 hover:bg-red-100" 
                : "bg-purple-50 border-purple-200 hover:bg-purple-100"
            )}
          >
            <div className="w-8 h-8 mb-2 flex items-center justify-center">
              {isRecording ? (
                <div className="w-6 h-6 bg-red-500 rounded"></div>
              ) : (
                <div className="w-6 h-6 bg-purple-500 rounded-full"></div>
              )}
            </div>
            <div className={cn("text-sm font-medium", 
              isRecording ? "text-red-800" : "text-purple-800"
            )}>
              {isRecording ? 'Stop' : 'Record'}
            </div>
          </button>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="flex flex-col items-center p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <div className="w-8 h-8 mb-2 bg-gray-500 rounded-sm flex items-center justify-center">
              <div className="w-5 h-6 bg-white rounded-sm"></div>
            </div>
            <div className="text-sm font-medium text-orange-800">Documents</div>
          </button>
        </div>
      )}

      {/* Desktop Upload Area */}
      {!isSwipeView && (
        <div
          className={cn(
            "upload-area p-8 border-2 border-dashed rounded-lg text-center transition-colors",
            dragActive ? "border-orange-500 bg-orange-50" : "border-gray-300 hover:border-gray-400",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <div className="upload-content">
            <div className="w-16 h-16 mb-4 bg-blue-500 rounded-lg flex items-center justify-center">
              <div className="w-10 h-8 bg-white rounded-sm"></div>
            </div>
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Drop files here or click to browse
            </div>
            <div className="text-gray-600 mb-4">
              Photos, documents, audio recordings
            </div>
            <div className="text-sm text-gray-500 space-y-1">
              <p>Max {formatFileSize(FILE_SIZE_LIMITS.maxFileSize)} per file</p>
              <p>Total limit: {formatFileSize(FILE_SIZE_LIMITS.maxTotalSize)}</p>
            </div>
          </div>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={Object.keys(MIME_TYPES).join(',')}
        onChange={handleFileInput}
        disabled={disabled}
        style={{ display: 'none' }}
      />

      {/* Recording Status */}
      {isRecording && (
        <div className={cn(
          "recording-status mt-4 p-4 bg-red-50 border border-red-200 rounded-lg",
          isSwipeView ? "mx-4" : ""
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="font-medium text-red-800">Recording Audio</span>
            </div>
            <div className="text-red-700 font-mono">
              {formatDuration(recordingTime)} / {formatDuration(FILE_SIZE_LIMITS.maxAudioDuration)}
            </div>
          </div>
          <button
            onClick={stopRecording}
            className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors w-full sm:w-auto"
          >
            ⏹️ Stop Recording
          </button>
        </div>
      )}

      {/* Evidence List */}
      {evidence.length > 0 && (
        <div className={cn("evidence-list mt-6", isSwipeView ? "px-4" : "")}>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Attached Evidence ({evidence.length})
          </h4>
          
          <div className={cn(
            "evidence-items space-y-3",
            isSwipeView ? "grid grid-cols-1" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          )}>
            {evidence.map(item => (
              <div key={item.id} className="evidence-item bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-start p-4">
                  {/* Preview/Icon */}
                  <div className="flex-shrink-0 mr-3">
                    {item.thumbnail ? (
                      <img 
                        src={item.thumbnail} 
                        alt={item.filename} 
                        className="w-12 h-12 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => showImagePreview(item)}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                        {getFileIcon(item.type)}
                      </div>
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-gray-900 truncate">{item.filename}</h5>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span>{formatFileSize(item.size)}</span>
                      {item.metadata?.duration && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-terracotta-500 rounded-full"></div>
                          <span>{formatDuration(Math.round(item.metadata.duration))}</span>
                        </div>
                      )}
                      {item.metadata?.dimensions && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-steel-500 rounded-sm"></div>
                          <span>{item.metadata.dimensions.width}×{item.metadata.dimensions.height}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                        item.uploadStatus === 'complete' ? "bg-green-100 text-green-800" :
                        item.uploadStatus === 'pending' || item.uploadStatus === 'uploading' ? "bg-orange-100 text-orange-800" :
                        "bg-red-100 text-red-800"
                      )}>
                        {getStatusIcon(item.uploadStatus)} {item.uploadStatus}
                      </span>
                      
                      {offlineManager.isOffline() && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-coral-500 rounded"></div>
                            <span>Offline</span>
                          </div>
                        </span>
                      )}
                    </div>

                    {/* Upload Progress */}
                    {uploadProgress[item.id] !== undefined && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress[item.id]}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress[item.id]}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-2">
                    {item.uploadStatus === 'failed' && (
                      <button
                        onClick={() => retryUpload(item)}
                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Retry upload"
                      >
                        <div className="w-4 h-4 bg-steel-500 rounded-full animate-spin"></div>
                      </button>
                    )}
                    <button
                      onClick={() => removeEvidence(item.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove evidence"
                    >
                      <div className="w-4 h-4 bg-coral-500 rounded-sm"></div>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Supported formats info */}
      <div className={cn("format-info mt-6", isSwipeView ? "px-4" : "")}>
        <details className="bg-gray-50 rounded-lg">
          <summary className="p-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-sage-500 rounded-sm"></div>
              <span>Supported file formats</span>
            </div>
          </summary>
          <div className="p-4 pt-2 text-sm text-gray-600 space-y-2">
            <div>
              <strong className="text-gray-700">Images:</strong> {SUPPORTED_FILE_TYPES.images.join(', ')}
            </div>
            <div>
              <strong className="text-gray-700">Documents:</strong> {SUPPORTED_FILE_TYPES.documents.join(', ')}
            </div>
            <div>
              <strong className="text-gray-700">Audio:</strong> {SUPPORTED_FILE_TYPES.audio.join(', ')}
            </div>
          </div>
        </details>
      </div>

      {/* Modals */}
      <CameraModal />
      <ImagePreviewModal />
    </div>
  );
};